import { Injectable, type Type, inject } from "@angular/core";
import { ToastService } from "@firm360/libs/ui/lumina";
import { NgbModal, type NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import moment from "moment";
import {
    EmitEvent,
    Event,
    EventService,
} from "../../shared/events/events.service";
import {
    BillableItemType,
    type IBillableItem,
} from "../invoicing/billable-items/billable-item.model";
import { BillableItemService } from "../invoicing/billable-items/services/billable-item.service";
import { OpenTimersPollerService } from "./open-timers-poller.service";
import type { TimerPrefill } from "./prefill/timer-prefill";

const MODAL_OPTIONS = {
    size: "lg",
    backdrop: "static",
    keyboard: false,
} as const;

// Loaded on first use: this service is reachable from the eager app shell,
// and the modals are too heavy for the main bundle.
const loadTimeEntryModal = () =>
    import("../invoicing/billable-items/edit-modal/billable-item-modal.component").then(
        (module) => module.BillableItemModalComponent,
    );

const loadTimerFinishModal = () =>
    import("./components/billable-item-timer-finish-modal/billable-item-timer-finish-modal.component").then(
        (module) => module.BillableItemTimerFinishModalComponent,
    );

/**
 * What the timer controls do, shared by every surface that shows them so a
 * timer behaves the same wherever a user reaches it.
 */
@Injectable({ providedIn: "root" })
export class TimerActionsService {
    private readonly billableItemService = inject(BillableItemService);
    private readonly modalService = inject(NgbModal);
    private readonly eventService = inject(EventService);
    private readonly openTimersPoller = inject(OpenTimersPollerService);
    private readonly toast = inject(ToastService);
    private isLoadingModal = false;

    canResume(timer: IBillableItem): boolean {
        return this.billableItemService.canTimerBeResumed(timer);
    }

    formatPausedDuration(durationInSeconds: number): string {
        return moment.utc(durationInSeconds * 1000).format("H[h] mm[min]");
    }

    resume(timer: IBillableItem): void {
        this.billableItemService.resumeTimer(timer).subscribe({});
    }

    pause(timer: IBillableItem): void {
        this.billableItemService.pauseTimer(timer).subscribe({
            next: async () => {
                const modalRef = await this.openModal(loadTimerFinishModal);
                if (!modalRef) {
                    return;
                }
                modalRef.componentInstance.finishingTimer = false;
                modalRef.componentInstance.billableItem = timer;
                modalRef.result.then(
                    (result: [moment.Moment, string, number?]) => {
                        const [, description, taskId] = result;
                        this.billableItemService.updateTimer(
                            timer,
                            description,
                            taskId,
                        );
                    },
                    () => {},
                );
            },
        });
    }

    async finish(timer: IBillableItem): Promise<void> {
        const modalRef = await this.openModal(loadTimerFinishModal);
        if (!modalRef) {
            return;
        }
        modalRef.componentInstance.finishingTimer = true;
        modalRef.componentInstance.billableItem = timer;
        modalRef.result.then(
            (result: [moment.Moment, string, number?]) => {
                const [endTime, description, taskId] = result;
                this.billableItemService
                    .finishTimer(timer, endTime, description, taskId)
                    .subscribe({});
            },
            () => {},
        );
    }

    async addTime(prefill: TimerPrefill): Promise<void> {
        const modalRef = await this.openTimeEntryModal(prefill);
        if (!modalRef) {
            return;
        }
        modalRef.componentInstance.saved.subscribe(() =>
            this.announceTimeEntryChanged(prefill),
        );
    }

    async startTimer(prefill: TimerPrefill): Promise<void> {
        const modalRef = await this.openTimeEntryModal(prefill);
        if (!modalRef) {
            return;
        }
        modalRef.componentInstance.startTimerMode = true;
        modalRef.componentInstance.saved.subscribe(() => {
            this.openTimersPoller.requestRefresh();
            this.announceTimeEntryChanged(prefill);
        });
    }

    private async openTimeEntryModal({
        client,
        project,
    }: TimerPrefill): Promise<NgbModalRef | undefined> {
        const modalRef = await this.openModal(loadTimeEntryModal);
        if (!modalRef) {
            return undefined;
        }
        modalRef.componentInstance.type = BillableItemType.HOURLY;
        modalRef.componentInstance.hasClientContext = !!client;
        modalRef.componentInstance.clientId = client ? client.id : "";
        modalRef.componentInstance.projectId = project ? project.id : "";
        return modalRef;
    }

    /**
     * Resolves without a modal when the component fails to load, or when
     * another modal is still loading: once open, a static backdrop swallows
     * further clicks, but nothing covers the controls while a chunk downloads.
     */
    private async openModal(
        load: () => Promise<Type<unknown>>,
    ): Promise<NgbModalRef | undefined> {
        if (this.isLoadingModal) {
            return undefined;
        }
        this.isLoadingModal = true;
        let component: Type<unknown>;
        try {
            component = await load();
        } catch {
            this.toast.error("Couldn't open the time entry", {
                description: "Reload the page, then try again.",
            });
            return undefined;
        } finally {
            this.isLoadingModal = false;
        }
        return this.modalService.open(component, MODAL_OPTIONS);
    }

    private announceTimeEntryChanged({ project }: TimerPrefill): void {
        this.eventService.emit(
            new EmitEvent(
                Event.PROJECT_TIME_ENTRY_CHANGED,
                project?.id?.toString(),
            ),
        );
    }
}
